import { format, startOfMonth } from './mes';

/**
 * A régua do faturamento, para os dois lados da conta de cancelamento.
 *
 * Devolve os nomes (UPPER+TRIM) dos produtos com o KPI `FAT. TOTAL` ligado —
 * a MESMA fonte da Visão Prime, da Planilha, da comissão e das filas do Kanban
 * (`triagem/flagsFaturamento.ts`). Mora aqui porque o deflator e o detalhe
 * (`useCancelamentoDetalhe`) TÊM que medir a mesma coisa: se divergirem, a
 * lista não fecha com o %.
 *
 * Set vazio = não carregou; quem chama conta tudo, em vez de zerar a base.
 */
export async function fetchProdutosFaturamento(supabase: any): Promise<Set<string>> {
  const { data } = await supabase.from('produtos').select('nome, fat_total');
  const set = new Set<string>();
  for (const p of ((data ?? []) as Array<{ nome: string | null; fat_total: boolean | null }>)) {
    if (p.fat_total && p.nome) set.add(String(p.nome).toUpperCase().trim());
  }
  return set;
}

/** Item conta como faturamento? Set vazio (falha na carga) = conta tudo. */
export function contaFaturamento(produto: string | null | undefined, regua: Set<string>): boolean {
  if (regua.size === 0) return true;
  return regua.has(String(produto || '').toUpperCase().trim());
}

// ============================================================================
// DEFLATOR DE CANCELAMENTO — em lote (Douglas 31/07/2026)
//
// "deflator de comissão de 10% se o consultor tiver mais de 50% dos seus
//  pedidos cancelados... o % é sempre com base na comissão que ele vai ganhar.
//  Ex.: direito a R$ 5.000, deflator 10% — perde R$ 500."
//
// Dois percentuais, como ele desenhou:
//   limite_pct   → o gatilho (quanto pode cancelar antes de doer)
//   deflator_pct → o castigo, aplicado sobre a COMISSÃO
//
// Em lote pelo mesmo motivo do `useConvergenciaSaldoLote`: a tela mostra dezenas
// de pessoas e resolver uma a uma multiplicaria as idas ao banco. Aqui são 2
// consultas para a empresa inteira.
// ============================================================================

/** Base do % de cancelamento: só o que já teve desfecho (ativo ou cancelado). */
export const QUADROS_BASE_CANCELAMENTO = ['QUALIDADE', 'PEDIDO_CANCELADO'];

export function entraNaBaseCancelamento(quadro: string | null | undefined): boolean {
  return QUADROS_BASE_CANCELAMENTO.includes(String(quadro || ''));
}

export type BaseCalculo = 'faturamento' | 'item' | 'pedido';

interface ConfigRow {
  cargo: string | null;
  competencia: string;
  limite_pct: number | null;
  deflator_pct: number | null;
  base_calculo: string | null;
  ativo: boolean | null;
}

interface ItemRow {
  consultor_id: string | null;
  pedido_id: string | null;
  produto: string | null;
  equipe_nome: string | null;
  subtotal: number | null;
  quadro_atual: string | null;
}

/**
 * Equipe que NÃO entra na conta de cancelamento de ninguém.
 *
 * (Douglas 21/08) "não pode entrar a equipe diretoria pra ela, e o restante é
 * tudo dela". A comissão JÁ excluía a DIRETORIA do faturamento que paga
 * (`diretoriaIdSet` em useVisaoComissaoSimulada) — só o deflator não excluía, e
 * então descontava de uma comissão SEM Diretoria usando um % medido COM ela:
 * media um universo e castigava outro.
 *
 * O filtro mora AQUI, e não nos três lugares que chamam `calcular`
 * (useVisaoComissaoSimulada, useResumoComissoesEquipe, VisaoComissoes), porque
 * régua em três cópias vira três réguas no primeiro que alguém esquecer.
 * Mesma exclusão que as views de convergência já fazem desde junho.
 */
const EQUIPES_FORA_DO_CANCELAMENTO = new Set(['DIRETORIA']);

export interface DeflatorPessoa {
  /** % de cancelamento medido na base configurada. */
  pctCancelado: number;
  /** Gatilho vigente para o cargo. */
  limitePct: number;
  /** % que sai da comissão quando estoura. */
  deflatorPct: number;
  base: BaseCalculo;
  /** Passou do gatilho? */
  estourou: boolean;
  /** R$ descontados da comissão. Zero quando não estourou. */
  desconto: number;
  /** Números que sustentam o %, para a tela poder explicar. */
  vendido: number;
  cancelado: number;
}

export interface DeflatorLote {
  configPorCargo: Map<string, { limite: number; deflator: number; base: BaseCalculo }>;
  /**
   * Calcula o deflator de uma pessoa.
   * @param consultorIds escopo — consultor = [ele]; gestor = hierarquia dele
   * @param cargo        CONSULTOR | SUPERVISOR | COORDENADOR | GERENTE
   * @param comissao     comissão JÁ apurada, sobre a qual o deflator incide
   */
  calcular: (consultorIds: string[], cargo: string, comissao: number) => DeflatorPessoa;
}

const VAZIO_PESSOA: DeflatorPessoa = {
  pctCancelado: 0, limitePct: 0, deflatorPct: 0, base: 'faturamento',
  estourou: false, desconto: 0, vendido: 0, cancelado: 0,
};

const VAZIO: DeflatorLote = {
  configPorCargo: new Map(),
  calcular: () => VAZIO_PESSOA,
};

export async function fetchDeflatorCancelamentoLote(competencia: Date, supabase: any): Promise<DeflatorLote> {
  const mes = format(startOfMonth(competencia), 'yyyy-MM');
  const mesDate = `${mes}-01`;

  const [cfgRes, itensRes, regua] = await Promise.all([
    supabase.from('deflator_cancelamento_config' as never)
      .select('cargo, competencia, limite_pct, deflator_pct, base_calculo, ativo')
      .lte('competencia', mesDate),
    // (Douglas 25/09/2026) A base é o que já teve DESFECHO: ATIVO + CANCELADO.
    // O que ainda anda na esteira sai da conta — no último dia ele vira
    // backlog do mês seguinte, saía da base e o % pulava de uma vez.
    supabase.from('v_planilha_mae' as never)
      .select('consultor_id, pedido_id, produto, equipe_nome, subtotal, quadro_atual')
      .eq('mes_trabalho', mes)
      .in('quadro_atual', QUADROS_BASE_CANCELAMENTO)
      .not('coluna_atual', 'ilike', 'ESTORNO%'),
    fetchProdutosFaturamento(supabase),
  ]);

  // Carry-forward por cargo: a config mais recente com competencia <= o mês.
  const maisRecente = new Map<string, ConfigRow>();
  for (const row of ((cfgRes.data ?? []) as ConfigRow[])) {
    const cargo = String(row.cargo ?? '').toUpperCase();
    const atual = maisRecente.get(cargo);
    if (!atual || String(row.competencia) > String(atual.competencia)) maisRecente.set(cargo, row);
  }
  const configPorCargo = new Map<string, { limite: number; deflator: number; base: BaseCalculo }>();
  for (const [cargo, row] of maisRecente) {
    configPorCargo.set(cargo, {
      limite: row.ativo === false ? Infinity : Number(row.limite_pct ?? 50),
      deflator: row.ativo === false ? 0 : Number(row.deflator_pct ?? 0),
      base: (row.base_calculo as BaseCalculo) ?? 'faturamento',
    });
  }

  // Agrega por consultor: total e cancelado, nas três bases de uma vez.
  interface Agg { fatTotal: number; fatCanc: number; itens: number; itensCanc: number; pedidos: Set<string>; pedidosCanc: Set<string> }
  const porConsultor = new Map<string, Agg>();
  /** Quem é da DIRETORIA: sai do agregado de gestor, mas mantém a régua própria. */
  const idsForaDoAgregado = new Set<string>();
  // Base = FATURAMENTO, nos dois lados. Antes daqui saía só o 'NÃO PAGO'
  // (presença-só: TT FIXA, AGENDA*, TOTALIZAÇÃO...) e o APARELHO continuava
  // entrando — o que corria CONTRA a régua e a favor de quem vende aparelho:
  // R$ 16.398,00 de dois celulares engordavam o `vendido` e DILUÍAM o % de
  // cancelamento. Em agosto/2026 isso segurava o Filipi em 10% (o real era 81%)
  // e a equipe FRESH em 28% (o real era 56%), com o limite em 40% — os dois
  // escapavam do deflator por causa de aparelho. Agora a régua é uma só:
  // `produtos.fat_total`, a mesma da Visão Prime e da comissão.
  for (const r of ((itensRes.data ?? []) as ItemRow[])) {
    if (!r.consultor_id) continue;
    if (!contaFaturamento(r.produto, regua)) continue;
    if (EQUIPES_FORA_DO_CANCELAMENTO.has(String(r.equipe_nome || '').toUpperCase().trim())) {
      idsForaDoAgregado.add(r.consultor_id);
    }
    let a = porConsultor.get(r.consultor_id);
    if (!a) {
      a = { fatTotal: 0, fatCanc: 0, itens: 0, itensCanc: 0, pedidos: new Set(), pedidosCanc: new Set() };
      porConsultor.set(r.consultor_id, a);
    }
    const v = Number(r.subtotal || 0);
    const cancelado = r.quadro_atual === 'PEDIDO_CANCELADO';
    a.fatTotal += v; a.itens += 1;
    if (r.pedido_id) a.pedidos.add(r.pedido_id);
    if (cancelado) {
      a.fatCanc += v; a.itensCanc += 1;
      if (r.pedido_id) a.pedidosCanc.add(r.pedido_id);
    }
  }

  const calcular = (consultorIds: string[], cargo: string, comissao: number): DeflatorPessoa => {
    const cfg = configPorCargo.get(String(cargo || '').toUpperCase());
    if (!cfg || !Number.isFinite(cfg.limite) || cfg.deflator <= 0) return VAZIO_PESSOA;

    // A DIRETORIA sai do AGREGADO de gestor — não da conta dela mesma.
    // (Douglas 21/08) "não pode entrar a equipe diretoria pra ela". Escopo de
    // uma pessoa só continua inteiro: o consultor da Diretoria tem comissão
    // individual e segue com a régua dele. O que ele não faz é entrar no % da
    // gerência, que paga sobre um faturamento onde ele não está.
    const todosIds = consultorIds.filter(Boolean);
    const ids = new Set(
      todosIds.length > 1 ? todosIds.filter((id) => !idsForaDoAgregado.has(id)) : todosIds,
    );
    let fatTotal = 0, fatCanc = 0, itens = 0, itensCanc = 0;
    const pedidos = new Set<string>(), pedidosCanc = new Set<string>();
    for (const id of ids) {
      const a = porConsultor.get(id);
      if (!a) continue;
      fatTotal += a.fatTotal; fatCanc += a.fatCanc;
      itens += a.itens; itensCanc += a.itensCanc;
      a.pedidos.forEach((p) => pedidos.add(p));
      a.pedidosCanc.forEach((p) => pedidosCanc.add(p));
    }

    const [vendido, cancelado] =
      cfg.base === 'item'   ? [itens, itensCanc] :
      cfg.base === 'pedido' ? [pedidos.size, pedidosCanc.size] :
                              [fatTotal, fatCanc];

    // Sem venda nenhuma não há o que deflacionar — 0/0 não é 100%.
    if (vendido <= 0) return { ...VAZIO_PESSOA, limitePct: cfg.limite, deflatorPct: cfg.deflator, base: cfg.base };

    const pct = (cancelado / vendido) * 100;
    const estourou = pct > cfg.limite;
    // Incide sobre a COMISSÃO, como o Douglas definiu — nunca sobre faturamento,
    // e nunca deixa o valor negativo.
    const desconto = estourou ? Math.max(0, comissao) * (cfg.deflator / 100) : 0;

    return {
      pctCancelado: pct,
      limitePct: cfg.limite,
      deflatorPct: cfg.deflator,
      base: cfg.base,
      estourou,
      desconto,
      vendido,
      cancelado,
    };
  };

  return { configPorCargo, calcular };
}


export { VAZIO as DEFLATOR_LOTE_VAZIO };
